import React, { useState, useEffect, useRef } from "react"
import {
  CalendarEvent,
  MusicNoteBeamed,
  FileText,
  CameraVideo,
  Collection,
  Activity,
  People,
  InfoCircle,
} from "react-bootstrap-icons"
import { useLocation, useHistory } from "react-router-dom"
import { OtherBandsSection } from "@components/bands/OtherBandsSection"
import { PostSection } from "@components/bands/PostSection"
import ScrollerBandHeader from "@components/BandScreen/ScrollerBandHeader"
import { ShareUrlButton } from "@components/ShareUrlButton"
import { BandSocialIcons } from "@components/bands/BandSocialIcons"
import BandActivityEvents from "@components/BandScreen/BandActivityEvents"
import ConcertSection from "@components/BandScreen/ConcertSection"
import type { Band } from "@models/bands"
import type { ConcertDisplayConfig } from "@models/concertDisplay"
import type { AlbumDisplayConfig } from "@models/albumDisplay"
import type { HeadlineDisplayConfig } from "@models/headlineDisplay"
import type { VideoDisplayConfig } from "@models/videoDisplay"
import { isMobile } from "react-device-detect"
import { useBandPosts } from "@hooks/useBandPosts"
import BandPlaylistsSection from "@components/bands/BandPlaylistsSection"
import {
  useBandCreatedPlaylists,
  useBandFeaturedPlaylists,
} from "@hooks/useBandPlaylists"
import styles from "./styles.module.css"

interface ScrollerTemplateProps {
  band: Band
}

type SectionId =
  | "shows"
  | "music"
  | "posts"
  | "video"
  | "playlists"
  | "bio"
  | "activity"
  | "related"

type NavItem = { id: SectionId; label: string; icon: React.ReactElement }

const NAV_ITEMS: NavItem[] = [
  { id: "shows", label: "Shows", icon: <CalendarEvent size={18} /> },
  { id: "music", label: "Music", icon: <MusicNoteBeamed size={18} /> },
  { id: "posts", label: "Posts", icon: <FileText size={18} /> },
  { id: "video", label: "Video", icon: <CameraVideo size={18} /> },
  { id: "playlists", label: "Playlists", icon: <Collection size={18} /> },
  { id: "bio", label: "Bio", icon: <InfoCircle size={18} /> },
  { id: "activity", label: "Activity", icon: <Activity size={18} /> },
  { id: "related", label: "Related", icon: <People size={18} /> },
]

const DESKTOP_SECTION_IDS: SectionId[] = [
  "shows",
  "music",
  "posts",
  "video",
  "playlists",
  "bio",
  "related",
]
const MOBILE_SECTION_IDS: SectionId[] = [
  "shows",
  "music",
  "posts",
  "video",
  "playlists",
  "bio",
  "activity",
  "related",
]

const VALID_TABS: SectionId[] = [
  "shows",
  "music",
  "posts",
  "video",
  "playlists",
  "bio",
  "related",
  "activity",
]

// Fade the cover image to black over the first 600px of scrolling.
const IMAGE_FADE_DISTANCE = 600

const ScrollerTemplate: React.FC<ScrollerTemplateProps> = ({ band }) => {
  const location = useLocation()
  const history = useHistory()
  const tabFromUrl = new URLSearchParams(location.search).get(
    "tab",
  ) as SectionId | null
  const [activeSection, setActiveSection] = useState<SectionId>(
    tabFromUrl && VALID_TABS.includes(tabFromUrl) ? tabFromUrl : "shows",
  )
  const rootRef = useRef<HTMLDivElement>(null)
  const headerRef = useRef<HTMLDivElement>(null)
  const navRef = useRef<HTMLDivElement>(null)
  const navAnchorRef = useRef<HTMLDivElement>(null)
  const backgroundRef = useRef<HTMLDivElement>(null)
  const backgroundFadeRef = useRef<HTMLDivElement>(null)
  const scrollContainerRef = useRef<HTMLElement | null>(null)
  const updateScrollEffectsRef = useRef<() => void>(() => {})
  const [headerHeight, setHeaderHeight] = useState(0)
  const [sectionNavHeight, setSectionNavHeight] = useState(0)
  const [showBackline, setShowBackline] = useState(
    new URLSearchParams(location.search).get("backline") === "true",
  )

  useEffect(() => {
    const header = headerRef.current
    const nav = navRef.current
    if (!header || !nav) return

    const measure = () => {
      setHeaderHeight(header.getBoundingClientRect().height)
      setSectionNavHeight(nav.getBoundingClientRect().height)
    }
    measure()

    if (typeof ResizeObserver !== "undefined") {
      const observer = new ResizeObserver(measure)
      observer.observe(header)
      observer.observe(nav)
      return () => observer.disconnect()
    }

    window.addEventListener("resize", measure)
    return () => window.removeEventListener("resize", measure)
  }, [])

  useEffect(() => {
    const root = rootRef.current
    const header = headerRef.current
    const anchor = navAnchorRef.current
    const background = backgroundRef.current
    const backgroundFade = backgroundFadeRef.current
    if (!root || !header || !anchor || !background || !backgroundFade) return

    let node = root.parentElement
    while (node) {
      const overflowY = getComputedStyle(node).overflowY
      if (overflowY === "auto" || overflowY === "scroll") break
      node = node.parentElement
    }
    scrollContainerRef.current = node
    if (node) node.classList.add(styles.scrollContainer)

    const scroller: HTMLElement | Window = node ?? window
    let frame = 0
    const updateScrollEffects = () => {
      if (frame) return
      frame = requestAnimationFrame(() => {
        frame = 0
        const scrolled = node ? node.scrollTop : window.scrollY
        const progress = Math.min(
          1,
          Math.max(0, scrolled / IMAGE_FADE_DISTANCE),
        )
        const anchorTop = anchor.getBoundingClientRect().top
        const headerBottom = header.getBoundingClientRect().bottom

        backgroundFade.style.opacity = progress.toFixed(3)
        background.style.height = `${Math.max(headerBottom, anchorTop)}px`
        // The hero has scrolled up underneath the sticky header once the
        // anchor (placed right where the hero ends) reaches the header's
        // bottom edge - solid the header backdrop at that point instead of
        // showing fixed-background black through it. Computed every scroll/
        // resize frame from live geometry (like the two lines above) rather
        // than via a separate IntersectionObserver, which had to be torn
        // down and recreated any time headerHeight changed (e.g. header
        // content loading in asynchronously) and could report a stale
        // "not intersecting" snapshot from mid-layout-shift that then never
        // got a chance to correct itself until the next real scroll.
        root.style.setProperty(
          "--header-solid",
          anchorTop <= headerBottom ? "1" : "0",
        )
        root.style.setProperty(
          "--scroll-viewport-height",
          `${node?.clientHeight ?? window.innerHeight}px`,
        )
      })
    }

    updateScrollEffectsRef.current = updateScrollEffects
    updateScrollEffects()
    scroller.addEventListener("scroll", updateScrollEffects, { passive: true })
    window.addEventListener("resize", updateScrollEffects)
    return () => {
      if (frame) cancelAnimationFrame(frame)
      scroller.removeEventListener("scroll", updateScrollEffects)
      window.removeEventListener("resize", updateScrollEffects)
      if (node) node.classList.remove(styles.scrollContainer)
    }
  }, [])

  // Re-run the scroll-position-derived geometry (background height,
  // --header-solid) whenever the header/nav's own measured height changes -
  // e.g. header content (avatar, follower count) loading in asynchronously
  // after mount shifts where the anchor sits relative to the header, even
  // though no actual scroll or window resize happened.
  useEffect(() => {
    updateScrollEffectsRef.current()
  }, [headerHeight, sectionNavHeight])

  const concertDisplayConfig: ConcertDisplayConfig = {
    showLineup: "preview",
    showRelatedPosts: "hide",
    showComments: "hint",
  }
  const albumDisplayConfig: AlbumDisplayConfig = { showComments: "hint" }
  const headlineDisplayConfig: HeadlineDisplayConfig = {
    showComments: "hint",
    showRelatedPosts: "hide",
  }
  const videoDisplayConfig: VideoDisplayConfig = {
    showComments: "hint",
    showRelatedPosts: "hide",
  }

  const { data: albumData } = useBandPosts(band.id, "albums", false, 1, 1)
  const { data: headlineData } = useBandPosts(band.id, "headlines", false, 1, 1)
  const { data: videoData } = useBandPosts(band.id, "videos", false, 1, 1)
  const { data: futureConcertData } = useBandPosts(
    band.id,
    "future_concerts",
    false,
    1,
    1,
  )
  const { data: pastConcertData } = useBandPosts(
    band.id,
    "past_concerts",
    false,
    1,
    1,
  )
  const { data: createdPlaylistData } = useBandCreatedPlaylists(band.id, 1)
  const { data: featuredPlaylistData } = useBandFeaturedPlaylists(band.id, 1)

  // While loading (data === undefined) keep the tab visible to avoid flicker
  const hasPlaylists =
    !createdPlaylistData ||
    !featuredPlaylistData ||
    (createdPlaylistData as any).total_count > 0 ||
    (featuredPlaylistData as any).total_count > 0

  const hasBio = !!(
    band.bio ||
    (band.band_type === "venue" && band.backline_offerings?.trim().length > 0)
  )

  const tabVisible: Record<SectionId, boolean> = {
    shows:
      !futureConcertData ||
      !pastConcertData ||
      futureConcertData.total_count > 0 ||
      pastConcertData.total_count > 0,
    music: !albumData || albumData.total_count > 0,
    posts: !headlineData || headlineData.total_count > 0,
    video: !videoData || videoData.total_count > 0,
    playlists: hasPlaylists,
    bio: hasBio,
    related: true,
    activity: true,
  }

  const sectionIds = isMobile ? MOBILE_SECTION_IDS : DESKTOP_SECTION_IDS
  const activeTabVisible = tabVisible[activeSection]
  const firstVisibleSection = NAV_ITEMS.find(
    (item) => sectionIds.includes(item.id) && tabVisible[item.id],
  )?.id

  // If the active tab loses its content, fall back to the first visible tab
  useEffect(() => {
    if (!activeTabVisible && firstVisibleSection) {
      setActiveSection(firstVisibleSection)
      const params = new URLSearchParams(location.search)
      params.set("tab", firstVisibleSection)
      history.replace({ ...location, search: params.toString() })
    }
  }, [activeTabVisible, firstVisibleSection, history, location])

  const defaultBackgroundStatic =
    "https://bandnada.s3.amazonaws.com/static/external_assets/web/background_logo_bottom_left_gray.png"

  const bgSrc = band.cover_photo_url_large ?? defaultBackgroundStatic
  // A much smaller same-crop variant that loads near-instantly, shown while
  // the full-size cover image is still downloading so the hero never starts
  // on a flat black background on a slow connection or a big upload.
  const bgPlaceholderSrc = band.cover_photo_url_small
  const [largeImageLoaded, setLargeImageLoaded] = useState(false)
  const bgObjectPosition = band.cover_photo_position ?? "center"

  const handleSectionChange = (id: SectionId) => {
    setActiveSection(id)
    const params = new URLSearchParams(location.search)
    params.set("tab", id)
    history.replace({ ...location, search: params.toString() })

    const container = scrollContainerRef.current
    const anchor = navAnchorRef.current
    const header = headerRef.current
    if (!container || !anchor || !header) return
    const delta =
      anchor.getBoundingClientRect().top - header.getBoundingClientRect().bottom
    if (delta < 0) container.scrollTop += delta
  }

  const navItems = NAV_ITEMS.filter(
    (item) => sectionIds.includes(item.id) && tabVisible[item.id],
  )

  /* Page behavior derived from: https://codepen.io/yourpalnurav/pen/MLqGQj?editors=1100 */

  return (
    <div
      ref={rootRef}
      className="col-span-12"
      style={
        {
          "--header-height": `${headerHeight}px`,
          "--section-nav-height": `${sectionNavHeight}px`,
        } as React.CSSProperties
      }
    >
      <div ref={backgroundRef} className={styles.background}>
        {bgPlaceholderSrc && (
          <img
            className={`${styles.backgroundImage} ${styles.backgroundImagePlaceholder}`}
            src={bgPlaceholderSrc}
            alt=""
            style={{ objectPosition: bgObjectPosition }}
          />
        )}
        <img
          className={`${styles.backgroundImage} ${
            bgPlaceholderSrc
              ? `${styles.backgroundImageFull} ${
                  largeImageLoaded ? styles.loaded : ""
                }`
              : ""
          }`}
          src={bgSrc}
          alt=""
          style={{ objectPosition: bgObjectPosition }}
          onLoad={() => setLargeImageLoaded(true)}
        />
        <div className={styles.backgroundGradient} />
        <div ref={backgroundFadeRef} className={styles.backgroundFade} />
        <div className={styles.backgroundSolid} />
      </div>
      {/* Header 2 */}
      <div ref={headerRef} className={styles.titleHeader}>
        <ScrollerBandHeader
          band={band}
          headerMode="classic"
          showBackline={showBackline}
          setShowBackline={setShowBackline}
        />
      </div>

      {/* Content 2 */}
      <div className="">
        {/* Spacer matches the visible hero below the sticky title bar. */}
        <div className={styles.heroSpacer} />

        {/* Header 3 - Child of Content 2 */}
        <div ref={navAnchorRef} />
        <div
          ref={navRef}
          className={`${styles.sectionNav} bg-white border-b border-gray-200 shadow-sm`}
        >
          {/* Social icons row — mobile only */}
          <div className="flex md:hidden items-center px-4 py-2 border-b border-gray-100 gap-3">
            <div className="flex items-center gap-3 overflow-hidden min-w-0 flex-1">
              <BandSocialIcons
                band={band}
                color="text-gray-700"
                margins="mr-3"
                includeShareIcon={false}
              />
            </div>
            <div className="shrink-0">
              <ShareUrlButton
                nadaUrl={`${window.location.protocol}//${window.location.host}${band.band_path}?follow=true&utm_source=band_header&utm_medium=share`}
                renderMode="button"
                className="mb-0"
              />
            </div>
          </div>

          {/* Nav row (+ social icons on desktop) */}
          <div className="flex items-center">
            <nav className="flex overflow-x-auto flex-1">
              {navItems.map(({ id, label, icon }) => (
                <button
                  key={id}
                  onClick={() => handleSectionChange(id)}
                  title={label}
                  className={[
                    "flex-shrink-0 flex items-center justify-center transition-colors focus:outline-none",
                    "px-4 py-3 md:px-6",
                    activeSection === id
                      ? "border-b-2 border-black text-black"
                      : "text-gray-500 hover:text-black",
                  ].join(" ")}
                >
                  <span className="mr-0 md:mr-2">{icon}</span>
                  <span className="hidden md:inline text-sm font-semibold uppercase tracking-wide">
                    {label}
                  </span>
                </button>
              ))}
            </nav>
            <div className="hidden md:flex px-4 shrink-0 gap-3 items-center">
              <BandSocialIcons
                band={band}
                color="text-gray-700"
                margins="mr-3"
                includeShareIcon={false}
              />
              <ShareUrlButton
                nadaUrl={`${window.location.protocol}//${window.location.host}${band.band_path}?follow=true&utm_source=band_header&utm_medium=share`}
                renderMode="button"
                className="mb-0"
              />
            </div>
          </div>
        </div>

        {/* Content 3 */}
        <div className="px-3 lg:pl-4 lg:pr-0 bg-white min-h-screen">
          <div className="grid grid-cols-8 gap-6">
            <div className="col-span-8 lg:col-span-5 py-6">
              {activeSection === "shows" && (
                <>
                  <ConcertSection
                    bandId={band.id}
                    bandName={band.name}
                    contentType="future_concerts"
                    pageSize={20}
                    noResultsMessage="No Shows Announced. See Past Shows Below"
                    config={concertDisplayConfig}
                  />
                  <div className="flex items-center gap-3 my-6">
                    <div className="flex-1 border-t border-gray-300" />
                    <p className="text-xs text-gray-400 italic whitespace-nowrap">
                      Past {band.name} Shows Only Below This Point
                    </p>
                    <div className="flex-1 border-t border-gray-300" />
                  </div>
                  <ConcertSection
                    bandId={band.id}
                    bandName={band.name}
                    contentType="past_concerts"
                    pageSize={20}
                    noResultsMessage={`${band.name} doesn't have any past shows`}
                    config={concertDisplayConfig}
                  />
                </>
              )}
              {activeSection === "music" && (
                <PostSection
                  bandId={band.id}
                  bandName={band.name}
                  contentType="albums"
                  pageSize={20}
                  colClass="grid-cols-1"
                  variant="short"
                  albumConfig={albumDisplayConfig}
                  noResultsMessage={`${band.name} doesn't have any music`}
                />
              )}
              {activeSection === "posts" && (
                <PostSection
                  bandId={band.id}
                  bandName={band.name}
                  contentType="headlines"
                  pageSize={20}
                  colClass="grid-cols-1"
                  variant="short"
                  headlineConfig={headlineDisplayConfig}
                  noResultsMessage={`${band.name} doesn't have any posts`}
                />
              )}
              {activeSection === "video" && (
                <PostSection
                  bandId={band.id}
                  bandName={band.name}
                  contentType="videos"
                  pageSize={20}
                  colClass="grid-cols-1"
                  variant="short"
                  videoConfig={videoDisplayConfig}
                  noResultsMessage={`${band.name} doesn't have any videos`}
                />
              )}
              {activeSection === "playlists" && (
                <BandPlaylistsSection bandId={band.id} bandName={band.name} />
              )}
              {activeSection === "bio" && (
                <div>
                  {band.band_type === "venue" &&
                    band.backline_offerings?.trim().length > 0 && (
                      <button
                        onClick={() => setShowBackline(true)}
                        className="text-blue-600 font-bold focus:outline-none text-sm mb-4 block"
                      >
                        ABOUT & BACKLINE
                      </button>
                    )}
                  {band.bio && (
                    <p className="text-sm italic text-gray-700 leading-relaxed">
                      {band.bio}
                    </p>
                  )}
                </div>
              )}
              {activeSection === "activity" && isMobile && (
                <BandActivityEvents band={band} />
              )}
              {activeSection === "related" && (
                <OtherBandsSection
                  bandId={String(band.id)}
                  bandName={band.name}
                  colClass="lg:grid-cols-6 grid-cols-2"
                  pageSize={30}
                  showHeader={false}
                />
              )}
            </div>

            {!isMobile && (
              <div className={`${styles.activityColumn} col-span-3`}>
                <BandActivityEvents band={band} />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default ScrollerTemplate
